import React from 'react'
import PropTypes from 'prop-types'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

const transformHourlyCallsData = (data, valueKey = 'calls', isAnalyticsOverview = false) => {
  // Support analytics thunk shape: { success: true, data: { total, trends: [{ _id, count }] } }
  if (isAnalyticsOverview && data && (data.data || data.trends)) {
    const payload = data.data || data
    const totalCalls =
      typeof payload.total === 'number' ? payload.total : typeof data.callsCount === 'number' ? data.callsCount : 0
    const trends = Array.isArray(payload.trends) ? payload.trends : Array.isArray(data.trends) ? data.trends : null

      if (trends && trends.length > 0) {
        const mapped = trends
          .map(item => {
            if (!item || !item._id) return null
            let dt
            try {
              dt = new Date(item._id)
            } catch {
              dt = null
            }

            if (dt && !isNaN(dt.getTime())) {
              const isMidnight = dt.getHours() === 0 && dt.getMinutes() === 0 && dt.getSeconds() === 0
              if (isMidnight) {
                const label = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                return { time: label, count: item.count || 0, ts: dt.getTime() }
              }

              // raw HH:00 label
              const hourLabel = `${dt.getHours()}:00`
              return { time: hourLabel, count: item.count || 0, ts: dt.getTime() }
            }

            return { time: String(item._id), count: item.count || 0 }
          })
          .filter(Boolean)

        mapped.sort((a, b) => {
          if (a.ts && b.ts) return a.ts - b.ts
          if (a.ts) return -1
          if (b.ts) return 1
          return parseTimeForSorting(a.time) - parseTimeForSorting(b.time)
        })

        return mapped
      }
    // fallback to synthetic distribution if no trends present

    const hourlyDistribution = [
      { time: '12:00', percentage: 0.01 }, // Very low at noon
      { time: '13:00', percentage: 0.08 }, // Gradual increase
      { time: '14:00', percentage: 0.5 }, // Major peak around 2 PM
      { time: '15:00', percentage: 0.4 }, // Slight dip
      { time: '16:00', percentage: 0.58 }, // Highest peak around 4 PM
      { time: '17:00', percentage: 0.48 }, // Decline starts
      { time: '18:00', percentage: 0.01 }, // Back to very low
    ]

    return hourlyDistribution.map(hour => ({
      time: hour.time,
      count: Math.round(totalCalls * hour.percentage),
      activeSessions: 0,
      uniqueUsers: 0,
      deviceBreakdown: [],
      originalHour: hour.time,
    }))
  }

  if (data && data.hourlyCalls && Array.isArray(data.hourlyCalls)) {
    return data.hourlyCalls
      .map(call => ({
        time: formatHourForChart(call.hour),
        count: call[valueKey] || 0,
        activeSessions: call.activeSessions || 0,
        uniqueUsers: call.uniqueUsers || 0,
        deviceBreakdown: call.deviceBreakdown || [],
        originalHour: call.hour,
      }))
      .filter(call => call.time !== null)
  }

  if (data && data.calls && Array.isArray(data.calls)) {
    return data.calls
      .map(call => {
        const hour = call.createdAt ? new Date(call.createdAt).getHours() : null
        const formattedTime = hour !== null ? formatHourForChart(`${hour}:00`) : null

        if (formattedTime === null) return null

        return {
          time: formattedTime,
          count: 1,
          activeSessions: call.isActive ? 1 : 0,
          uniqueUsers: 1,
          deviceBreakdown: [call.deviceType] || [],
          originalHour: `${hour}:00`,
          browser: call.browser,
          deviceType: call.deviceType,
          os: call.os,
        }
      })
      .filter(call => call !== null)
      .reduce((acc, call) => {
        const existingIndex = acc.findIndex(item => item.time === call.time)
        if (existingIndex >= 0) {
          acc[existingIndex].count += call.count
          acc[existingIndex].activeSessions += call.activeSessions
          acc[existingIndex].uniqueUsers += call.uniqueUsers
        } else {
          acc.push(call)
        }
        return acc
      }, [])
      .sort((a, b) => {
        const timeA = parseTimeForSorting(a.time)
        const timeB = parseTimeForSorting(b.time)
        return timeA - timeB
      })
  }

  if (data && typeof data.callsCount === 'number' && !isAnalyticsOverview) {
    const totalCalls = data.callsCount
    const businessHours = [
      { time: '7 AM', percentage: 0.05 },
      { time: '8 AM', percentage: 0.12 },
      { time: '9 AM', percentage: 0.15 },
      { time: '10 AM', percentage: 0.18 },
      { time: '11 AM', percentage: 0.16 },
      { time: '12 PM', percentage: 0.1 },
      { time: '1 PM', percentage: 0.08 },
      { time: '2 PM', percentage: 0.06 },
      { time: '3 PM', percentage: 0.05 }, // 5% - mid afternoon
      { time: '4 PM', percentage: 0.03 },
      { time: '5 PM', percentage: 0.02 },
    ]

    return businessHours.map(hour => ({
      time: hour.time,
      count: Math.round(totalCalls * hour.percentage),
      activeSessions: 0,
      uniqueUsers: 0,
      deviceBreakdown: [],
      originalHour: hour.time,
    }))
  }

  if (Array.isArray(data)) {
    return data
      .map(call => {
        if (call.time && call.count !== undefined) {
          const hourCheck = call.time
          const timeLabels = ['7 AM', '8 AM', '9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM']
          if (timeLabels.includes(hourCheck)) {
            return call
          }
          return null
        }

        const formattedTime = call.hour ? formatHourForChart(call.hour) : call.time
        if (formattedTime === null) return null

        return {
          time: formattedTime,
          count: call[valueKey] || call.count || 0,
          activeSessions: call.activeSessions || 0,
          uniqueUsers: call.uniqueUsers || 0,
          deviceBreakdown: call.deviceBreakdown || [],
          originalHour: call.hour,
        }
      })
      .filter(call => call !== null)
  }

  return []
}

const parseTimeForSorting = timeStr => {
  const [time, period] = timeStr.split(' ')
  let [hour] = time.split(':')
  hour = parseInt(hour, 10)

  if (period === 'PM' && hour !== 12) {
    hour += 12
  } else if (period === 'AM' && hour === 12) {
    hour = 0
  }

  return hour
}

const formatHourForChart = hour => {
  if (!hour) return ''

  const [hourStr] = hour.split(':')
  const hourNum = parseInt(hourStr, 10)

  // The original time-of-day filtering logic seems to focus on 7 AM to 5 PM
  // 0-6 are null (12 AM to 6 AM)
  if (hourNum < 7 || hourNum > 17) return null

  if (hourNum === 0) return '12 AM'
  if (hourNum >= 7 && hourNum < 12) return `${hourNum} AM`
  if (hourNum === 12) return '12 PM'
  if (hourNum > 12 && hourNum <= 17) return `${hourNum - 12} PM`

  return null
}

const fallbackData = [
  { time: '7 AM', count: 65 },
  { time: '8 AM', count: 85 },
  { time: '9 AM', count: 68 },
  { time: '10 AM', count: 48 },
  { time: '11 AM', count: 52 },
  { time: '12 PM', count: 42 },
  { time: '1 PM', count: 45 },
  { time: '2 PM', count: 62 },
  { time: '3 PM', count: 82 },
  { time: '4 PM', count: 65 },
  { time: '5 PM', count: 28 },
]

const CallCount = ({
  data = {}, // Initialize data to an object for safe destructuring
  title = '',
  height = 250,
  strokeColor = '#6A5ACD',
  showTitle = true,
  valueKey = 'count',
  tooltipLabel = 'Call Volume',
  domain = [0, 'dataMax'],
  showGrid = true,
  margin = { top: 20, right: 10, left: 0, bottom: 20 },
  isAnalyticsOverview = false,
  showTotalCount = true,
  showValue = null,
}) => {
  const transformedData = transformHourlyCallsData(data, valueKey, isAnalyticsOverview)
  const chartData = transformedData.length > 0 ? transformedData : fallbackData

  // Extract total from payload structure - prefer totalCalls, then total (matches MessageCount behavior)
  const payloadForTotal = data?.data || data
  const totalCount =
    (payloadForTotal && (payloadForTotal.totalCalls || payloadForTotal.total || payloadForTotal.total_calls || payloadForTotal.count)) ||
    data?.totalCalls ||
    0

  const displayValue = totalCount || (showValue !== null && showValue !== undefined ? showValue : 0)

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // The payload structure changed slightly in the overview, but payload[0].value still holds the count
      const value = payload[0].value
      return (
        <div className="bg-indigo-600 text-white px-3 py-2 rounded-lg shadow-lg border-0">
          <p className="text-sm font-medium">{`${tooltipLabel}`}</p>
          <p className="text-lg font-bold">{value.toLocaleString()}</p>
        </div>
      )
    }
    return null
  }

  // Analytics Overview Layout - ensure explicit height so ResponsiveContainer has space
  if (isAnalyticsOverview) {
    return (
      <div className="w-full flex flex-col">
        {/* Header with title and count in same line */}
        <div className="mb-2">
          <h3 className="text-sm font-medium text-gray-600">
            {title || 'Call Volume'}: <span className="font-bold">{displayValue.toLocaleString()}</span>
          </h3>
        </div>

        {/* Chart container with explicit height */}
        <div style={{ height: typeof height === 'number' ? height : 250 }} className="w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 20 }}>
              <CartesianGrid vertical={true} stroke="#d1d5db" horizontal={true} />
              <XAxis
                dataKey="time"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#666' }}
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                type="number"
                domain={domain}
                axisLine={false}
                tickLine={false}
                width={45}
                interval={0}
                tick={{ fontSize: 12, fill: '#666' }}
                tickFormatter={value => {
                  if (value === 0) return '0'
                  if (value >= 1000) return `${Math.round(value / 1000)}k`
                  return value.toString()
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line type="linear" dataKey="count" stroke={strokeColor} strokeWidth={2} dot={false} activeDot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    )
  }

  // Default Layout (existing layout) - matches MessageCount exactly
  return (
    <div className="w-full bg-white rounded-lg p-4 h-[400px] flex flex-col">
      {showTitle && title && (
        <h3 className="text-sm font-semibold mb-4 text-gray-800 flex items-center gap-2">
          {title}: 
          {/* FIX APPLIED HERE: Added totalCount.toLocaleString() */}
          {showTotalCount && totalCount > 0 && (
            <span className="text-sm font-bold text-gray-600">{totalCount.toLocaleString()}</span>
          )}
        </h3>
      )}
      <div className="flex-1 w-full h-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={margin}>
            {showGrid && <CartesianGrid vertical={true} stroke="#d1d5db" horizontal={true} />}
            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              padding={{ left: 10, right: 10 }}
            />
            <YAxis
              type="number"
              domain={domain}
              axisLine={false}
              tickLine={false}
              width={45}
              interval={0}
              tick={{ fontSize: 12, fill: '#666' }}
              tickFormatter={value => {
                if (value === 0) return '0'
                if (value >= 1000) return `${Math.round(value / 1000)}k`
                return value.toString()
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line type="linear" dataKey="count" stroke={strokeColor} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

CallCount.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  title: PropTypes.string,
  height: PropTypes.number,
  strokeColor: PropTypes.string,
  showTitle: PropTypes.bool,
  valueKey: PropTypes.string,
  tooltipLabel: PropTypes.string,
  domain: PropTypes.array,
  showGrid: PropTypes.bool,
  margin: PropTypes.object,
  isAnalyticsOverview: PropTypes.bool,
  showTotalCount: PropTypes.bool,
  showValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

CallCount.defaultProps = {
  data: {},
  title: '',
  height: 250,
  strokeColor: '#6A5ACD',
  showTitle: true,
  valueKey: 'count',
  tooltipLabel: 'Call Volume',
  domain: [0, 'dataMax'],
  showGrid: true,
  margin: { top: 20, right: 10, left: 0, bottom: 20 },
  isAnalyticsOverview: false,
  showTotalCount: false,
  showValue: null,
}

export default CallCount
